"use client";

import { Bell, BriefcaseBusiness, Calendar, PawPrint } from "lucide-react";
import { StatCard } from "../components/ui/stat-card";
import { AppointmentCalendar } from "../components/ui/calendar";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// Transform backend appointments to calendar format
function transformAppointments(appointments) {
  return appointments
    .filter((apt) => apt.datetime) // Filter out appointments without datetime
    .map((apt) => {
      const date = new Date(apt.datetime);
      const hours = date.getHours();
      const minutes = date.getMinutes();

      // Calculate hour index (0 = All Day, 1 = 9:00 AM, etc.)
      // Time slots: All Day, 9:00 AM, 10:00 AM, ..., 5:00 PM
      let hourIndex = hours - 8; // 9:00 AM = index 1
      if (hourIndex < 1) hourIndex = 0; // All Day for early appointments
      if (hourIndex > 10) hourIndex = 10; // Cap at 5:00 PM

      // Format time string
      const timeHours = hours % 12 || 12;
      const ampm = hours >= 12 ? "PM" : "AM";
      const timeString = `${timeHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;

      return {
        id: apt.id,
        date: date,
        day: date.getDay(), // 0-6 (Sunday-Saturday)
        hour: hourIndex,
        time: timeString,
        clientName: `${apt.pet.user.first_name} ${apt.pet.user.last_name}`,
        petName: apt.pet.name,
        status: apt.status,
      };
    });
}

// CSS Styles
const styles = `
  .dashboard-container {
    min-height: 100vh;
    background-color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  }

  /* Header Styles */
  .dashboard-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  @media (min-width: 768px) {
    .dashboard-header {
      padding: 1.5rem;
    }
  }

  .header-content h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.2;
  }

  @media (min-width: 768px) {
    .header-content h1 {
      font-size: 1.875rem;
    }
  }

  .header-content p {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0.25rem 0 0 0;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .notification-button {
    padding: 0.5rem;
    border-radius: 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .notification-button:hover {
    background-color: #f3f4f6;
  }

  .notification-button svg {
    width: 1.25rem;
    height: 1.25rem;
    color: #6b7280;
  }

  /* Stats Section */
  .stats-section {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  @media (min-width: 768px) {
    .stats-section {
      padding: 1.5rem;
    }
  }

  .stats-container {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }

  /* Loading and Error States */
  .dashboard-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background-color: #ffffff;
  }

  .loading-text {
    color: #6b7280;
    font-size: 1rem;
  }

  .error-text {
    color: #dc2626;
    font-size: 1rem;
  }

  /* Calendar Section */
  .calendar-section {
    padding: 1rem 1.5rem;
  }

  @media (min-width: 768px) {
    .calendar-section {
      padding: 1.5rem;
    }
  }

  /* Scrollbar Styling */
  .stats-container::-webkit-scrollbar {
    height: 4px;
  }

  .stats-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  .stats-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  .stats-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v1/veterinaries/dashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch dashboard data");
        }
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        console.error("[v0] Error fetching dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token]);

  const completedAppointments =
    dashboardData?.appointments?.filter((apt) => apt.status === "completed")
      .length || 0;

  const pendingAppointments =
    dashboardData?.appointments?.filter(
      (apt) => apt.status === "pending" || apt.status === "scheduled",
    ).length || 0;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-text">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-loading">
        <div className="error-text">Error: {error}</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-loading">
        <div className="loading-text">No data available</div>
      </div>
    );
  }

  // Transform appointments for calendar
  const calendarAppointments = transformAppointments(
    dashboardData.appointments,
  );

  return (
    <>
      <style>{styles}</style>
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Welcome back, {user.first_name}</h1>
          </div>
          <div className="header-actions">
            <button className="notification-button">
              <Bell />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-section">
          <div className="stats-container">
            <StatCard
              icon={BriefcaseBusiness}
              title="Services"
              value={dashboardData.services_count.toString()}
              variant="default"
            />
            <StatCard
              icon={Calendar}
              title="Total Appointments"
              value={dashboardData.appointments_count.toString()}
              variant="default"
            />
            <StatCard
              icon={Calendar}
              title="Completed"
              value={completedAppointments.toString()}
              trend={`${Math.round((completedAppointments / dashboardData.appointments_count) * 100)}%`}
              trendDirection="up"
              variant="success"
            />
            <StatCard
              icon={PawPrint}
              title="Pending"
              value={pendingAppointments.toString()}
              variant="warning"
            />
          </div>
        </div>

        {/* Calendar Section */}
        <div className="calendar-section">
          <AppointmentCalendar appointments={calendarAppointments} />
        </div>
      </div>
    </>
  );
}
